import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { BioweaveAuthError } from '@bioweave/api-client/errors';
import { handleAuthFailure, onSessionRenewed, onSessionWiped } from './lib/authExpiry';
import { tokenSentWith } from './lib/authToken';
import { ConsoleErrorBoundary } from './components/ConsoleErrorBoundary';
import { AuthProvider } from './providers/AuthProvider';
import { RequireAdvisor } from './providers/RequireAdvisor';
import { RequireAdmin } from './providers/RequireAdmin';
import { ConsoleLayout } from './admin/ConsoleLayout';
import { SignInPage } from './pages/SignInPage';
import { NotAuthorisedPage } from './pages/NotAuthorisedPage';
import { ReviewHomePage } from './pages/ReviewHomePage';
import { RegistryDetailPage } from './pages/RegistryDetailPage';
import { CompanionSafetyVisibilityPage } from './pages/CompanionSafetyVisibilityPage';
import { PeriReviewPage } from './pages/PeriReviewPage';
import { PeriReviewDetailPage } from './pages/PeriReviewDetailPage';
import { AuditPage } from './pages/AuditPage';
import { ProposalPage } from './pages/ProposalPage';
import { ModerationQueuePage } from './pages/ModerationQueuePage';
import { ModerationDocumentPage } from './pages/ModerationDocumentPage';
import { IngestedPage } from './pages/IngestedPage';
import { CompoundsPage } from './pages/CompoundsPage';
import { ModesPage } from './pages/ModesPage';
import { ModeDetailPage } from './pages/ModeDetailPage';
import { ModePanelsPage } from './pages/ModePanelsPage';
import { PanelsPage } from './pages/PanelsPage';
import { PanelDetailPage } from './pages/PanelDetailPage';
import { BiomarkersPage } from './pages/BiomarkersPage';
import { BiomarkerDetailPage } from './pages/BiomarkerDetailPage';
import { InstrumentsPage } from './pages/InstrumentsPage';
import { GenomicsPage } from './pages/GenomicsPage';
import { DomainsPage } from './pages/DomainsPage';
import { DomainDetailPage } from './pages/DomainDetailPage';
import { SchoolsPage } from './pages/SchoolsPage';
import { InterventionClassesPage } from './pages/InterventionClassesPage';
import { SafetyTablesPage } from './pages/SafetyTablesPage';
import { RenderersPage } from './pages/RenderersPage';
import { ModeContraindicationsPage } from './pages/ModeContraindicationsPage';
import { ModeTasksPage } from './pages/ModeTasksPage';
import { SafetyIndexPage } from './pages/SafetyIndexPage';
import { TasksPage } from './pages/TasksPage';
import { CpicPage } from './pages/CpicPage';
import { TestersPage } from './pages/TestersPage';
import { UnmatchedBiomarkersPage } from './pages/UnmatchedBiomarkersPage';
import { BetaConsolePage } from './pages/BetaConsolePage';
import { PathologyOrdersPage } from './pages/PathologyOrdersPage';

/**
 * A 401/403 from ANY query or mutation is treated as the credential being dead —
 * expired, revoked, or rotated out from under this tab.
 *
 * Both caches are wired, not just queries: a 401 on a PATCH is the same dead
 * credential as a 401 on a GET, and the console is full of mutations.
 *
 * `queryClient.clear()` matters as much as the sign-out. Without it the cached
 * results of the DEAD session stay in memory, so signing in as someone else
 * would render the previous user's data until each query refetched.
 */
function handleApiError(error: unknown): void {
  if (!(error instanceof BioweaveAuthError)) return;
  // A Cognito session gets one refresh first (handleAuthFailure); recovered, the
  // queries run again with the new access token. A request that left with a token since
  // replaced ('stale') runs again too: a first load still in flight when a recovery
  // landed is not refetched by that recovery's invalidate. Otherwise the session is over.
  // 'kept' does nothing: no refetch with a token that is still expired (Tiny's L2).
  // A MUTATION is never re-sent here; the operator sees it fail and repeats it.
  void handleAuthFailure(tokenSentWith(error)).then((outcome) => {
    if (outcome === 'recovered' || outcome === 'stale') void queryClient.invalidateQueries();
  });
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, staleTime: 15_000 } },
  queryCache: new QueryCache({ onError: handleApiError }),
  mutationCache: new MutationCache({ onError: handleApiError }),
});

// However a session is wiped (a 401, a refused refresh on the timer or the tab coming
// back), the dead session's cached data goes with it.
onSessionWiped(() => queryClient.clear());
// A session back from a Cognito blip: what failed meanwhile runs again with the new token.
onSessionRenewed(() => void queryClient.invalidateQueries());

const router = createBrowserRouter([
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/not-authorised', element: <NotAuthorisedPage /> },
  {
    element: <RequireAdvisor><ConsoleLayout /></RequireAdvisor>,
    children: [
      { path: '/', element: <ReviewHomePage /> },
      { path: '/beta', element: <BetaConsolePage /> },
      { path: '/review/peri', element: <PeriReviewPage /> },
      { path: '/review/peri/:participantRef', element: <PeriReviewDetailPage /> },
      { path: '/registries/:registryId', element: <RegistryDetailPage /> },
      { path: '/companion-safety', element: <CompanionSafetyVisibilityPage /> },
      { path: '/audit', element: <AuditPage /> },
      { path: '/proposals/reference-range', element: <ProposalPage /> },
      { path: '/moderation', element: <ModerationQueuePage /> },
      { path: '/moderation/:documentId', element: <ModerationDocumentPage /> },
      { path: '/ingested', element: <RequireAdmin><IngestedPage /></RequireAdmin> },
      { path: '/admin/compounds', element: <RequireAdmin><CompoundsPage /></RequireAdmin> },
      { path: '/admin/cpic', element: <RequireAdmin><CpicPage /></RequireAdmin> },
      { path: '/admin/testers', element: <RequireAdmin><TestersPage /></RequireAdmin> },
      { path: '/admin/unmatched-biomarkers', element: <RequireAdmin><UnmatchedBiomarkersPage /></RequireAdmin> },
      { path: '/admin/pathology', element: <RequireAdmin><PathologyOrdersPage /></RequireAdmin> },
      { path: '/admin/modes', element: <ModesPage /> },
      { path: '/admin/modes/:modeId', element: <ModeDetailPage /> },
      { path: '/admin/modes/:modeId/panels', element: <ModePanelsPage /> },
      { path: '/admin/modes/:modeId/contraindications', element: <ModeContraindicationsPage /> },
      { path: '/admin/modes/:modeId/tasks', element: <ModeTasksPage /> },
      { path: '/admin/safety-index', element: <SafetyIndexPage /> },
      { path: '/admin/tasks', element: <TasksPage /> },
      { path: '/admin/panels', element: <PanelsPage /> },
      { path: '/admin/panels/:panelId', element: <PanelDetailPage /> },
      { path: '/admin/biomarkers', element: <BiomarkersPage /> },
      { path: '/admin/biomarkers/:name', element: <BiomarkerDetailPage /> },
      { path: '/admin/instruments', element: <InstrumentsPage /> },
      { path: '/admin/genomics', element: <GenomicsPage /> },
      { path: '/admin/domains', element: <DomainsPage /> },
      { path: '/admin/domains/:domain', element: <DomainDetailPage /> },
      { path: '/admin/schools', element: <SchoolsPage /> },
      { path: '/admin/intervention-classes', element: <InterventionClassesPage /> },
      { path: '/admin/safety-tables', element: <SafetyTablesPage /> },
      { path: '/admin/renderers', element: <RenderersPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

export function App() {
  return (
    <ConsoleErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
    </ConsoleErrorBoundary>
  );
}
